import { AlertType, AloftPrice } from '@aloft/toolkit-shared';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import { CompAdjustments } from '../../../../redux/features/selectors/CompAdjustments.ts';

export const NET_ADJUSTMENT_THRESHOLD = 0.15;
export const GROSS_ADJUSTMENT_THRESHOLD = 25;

export const OVERLY_ADJUSTED_NET_TOOLTIP = 'Net adjustment exceeds 15%';
export const OVERLY_ADJUSTED_GROSS_TOOLTIP = 'Gross adjustment exceeds 25%';

export const OVERLY_ADJUSTED_ALERT_CRITERIA =
  'When net adjustment >15% OR gross adjustment >25% for any Comparable.';

export const OVERLY_ADJUSTED_TIPS =
  'Tips: Consider replacing with a less-adjusted Comp. If this remains the best available data, provide a market-supported explanation for its reliability.';

export const OVERLY_ADJUSTED_REFERENCE_TIPS = `${OVERLY_ADJUSTED_ALERT_CRITERIA} ${OVERLY_ADJUSTED_TIPS}`;

const OVERLY_ADJUSTED_ALERT_TYPES = new Set<AlertType>([
  'overlyAdjustedComparable',
  'overlyAdjustedComparableAiv',
  'overlyAdjustedComparableArv',
]);

export const isOverlyAdjustedAlertType = (type: AlertType | null | undefined): boolean =>
  type != null && OVERLY_ADJUSTED_ALERT_TYPES.has(type);

export const parsePercentValue = (value?: string): number | undefined => {
  if (!value || value === '-') return undefined;
  const parsed = Number.parseFloat(value.replace('%', ''));
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const exceedsNetAdjustmentThreshold = (
  netAdjustment: AloftPrice | undefined,
  salePrice: AloftPrice | undefined,
): boolean => {
  if (!netAdjustment || !salePrice) return false;
  const ratio = netAdjustment.ratio(salePrice);
  return ratio != null && Math.abs(ratio) > NET_ADJUSTMENT_THRESHOLD;
};

export const exceedsGrossAdjustmentThreshold = (grossAdjustmentPctValue?: string): boolean => {
  const parsed = parsePercentValue(grossAdjustmentPctValue);
  return parsed != null && parsed > GROSS_ADJUSTMENT_THRESHOLD;
};

export const isOverlyAdjusted = (
  compAdjustment: CompAdjustments | undefined,
  salePrice: AloftPrice | undefined,
): boolean => {
  if (!compAdjustment || !salePrice) return false;
  return (
    exceedsNetAdjustmentThreshold(compAdjustment.netAdjustment.adjustment, salePrice) ||
    exceedsGrossAdjustmentThreshold(compAdjustment.grossAdjustmentPct.value)
  );
};

export const getOverlyAdjustedTargetCellKey = (
  propertyId: string,
  compAdjustment: CompAdjustments | undefined,
  salePrice: AloftPrice | undefined,
): PropertyGridCellKey | undefined => {
  if (!compAdjustment || !salePrice) return undefined;
  if (exceedsNetAdjustmentThreshold(compAdjustment.netAdjustment.adjustment, salePrice)) {
    return `${propertyId}-netAdjustmentPct`;
  }
  if (exceedsGrossAdjustmentThreshold(compAdjustment.grossAdjustmentPct.value)) {
    return `${propertyId}-grossAdjustmentPct`;
  }
  return undefined;
};

export const getOverlyAdjustedTargetCellTooltip = (
  targetCellKey: PropertyGridCellKey | undefined,
): string | undefined => {
  if (targetCellKey?.endsWith('-netAdjustmentPct')) return OVERLY_ADJUSTED_NET_TOOLTIP;
  if (targetCellKey?.endsWith('-grossAdjustmentPct')) return OVERLY_ADJUSTED_GROSS_TOOLTIP;
  return undefined;
};
